import { PredictiveCampaignQuestionDto } from "@advanced-sales-technologies/backend-client/models/PredictiveCampaignQuestionDto";
import { PredictiveProductQuestionDto } from "@advanced-sales-technologies/backend-client/models/PredictiveProductQuestionDto";

import { FormConfigField, Question } from "@/types/DynamicForm.type";

type MergeOpts = {
  includeName?: boolean;
  includeHeaders?: boolean;
  alwaysInclude?: string[];
  includeWarnings?: boolean;
};

/**
 * Filter a local field-definition array and enrich each retained field
 * with its matching question DTO (if any).
 *
 * `F` must extend `FormConfigField` (so headers/warnings are allowed).
 * Returned array contains each field plus **optional** props from `Q`.
 */
export function mergeQuestionData<F extends FormConfigField, Q extends Question>(
  definition: readonly F[],
  questions: readonly Q[] | undefined,
  { includeName = true, includeHeaders = true, includeWarnings = false, alwaysInclude = [] }: MergeOpts = {},
): (F & Partial<Q>)[] {
  const filtered = definition.filter((field) => {
    // display-only checks
    if (includeHeaders && field.fieldType === "header") return true;
    if (includeWarnings && field.fieldType === "warning") return true;

    // interactive checks (only if field has `name` / `target`)
    if ("name" in field) {
      if (includeName && field.name === "name") return true;
      if (alwaysInclude.includes(field.name)) return true;
    }

    if ("target" in field && field.target) {
      return questions?.some((q) => q.target === field.target);
    }

    return false;
  });

  // merge
  return filtered.map((field) => {
    if ("target" in field && field.target) {
      const q = questions?.find((x) => x.target === field.target);
      return q ? { ...field, ...q } : field;
    }
    return field;
  }) as (F & Partial<Q>)[];
}

export const buildCurrentProductDefinition = (
  productFormDefinition: readonly FormConfigField[],
  questions: readonly PredictiveProductQuestionDto[] | undefined,
  opts?: MergeOpts,
) => mergeQuestionData(productFormDefinition, questions, opts);

export const buildCurrentDynamicFormDefinition = (
  campaignFormDefinition: readonly FormConfigField[],
  questions: readonly PredictiveCampaignQuestionDto[] | undefined,
  opts?: MergeOpts,
) => mergeQuestionData(campaignFormDefinition, questions, opts);
